import { Vec2d, VecLike } from './Vec2d';
import { LineSegmentLike } from './LineSegment';
import { GeometryUtils } from './geometry';

/**
 * 圆弧模型接口
 */
export interface CircleSegmentModel {
  center: VecLike;
  radius: number;
  startAngle: number;
  endAngle: number;
  /** 绘制方向：1 表示顺时针，-1 表示逆时针 */
  radian?: 1 | -1;
}

/**
 * 圆弧类型
 */
export type CircleSegmentLike = CircleSegment | CircleSegmentModel;

/**
 * 圆弧类
 * 表示2D平面中的一段圆弧，由圆心、半径、起始角度和结束角度定义
 */
export class CircleSegment {
  constructor(
    public center: Vec2d = new Vec2d(),
    public radius: number = 0,
    public startAngle: number = 0,
    public endAngle: number = 0,
    /** 绘制方向：1 表示顺时针，-1 表示逆时针 */
    public radian: 1 | -1 = -1,
  ) {}

  /**
   * 设置圆弧的所有属性
   * @param center 圆心
   * @param radius 半径
   * @param startAngle 起始角度（弧度）
   * @param endAngle 结束角度（弧度）
   * @param radian 绘制方向：1 表示顺时针，-1 表示逆时针
   */
  set(center: VecLike, radius: number, startAngle: number, endAngle: number, radian:1 | -1 = -1): CircleSegment {
    this.center.setTo(Vec2d.Cast(center));
    this.radius = radius;
    this.startAngle = startAngle;
    this.endAngle = endAngle;
    this.radian = radian;
    return this;
  }

  /**
   * 设置圆心
   */
  setCenter(center: VecLike): CircleSegment {
    this.center.setTo(Vec2d.Cast(center));
    return this;
  }

  /**
   * 设置半径
   */
  setRadius(radius: number): CircleSegment {
    this.radius = radius;
    return this;
  }

  /**
   * 设置起始角度
   */
  setStartAngle(startAngle: number): CircleSegment {
    this.startAngle = startAngle;
    return this;
  }

  /**
   * 设置结束角度
   */
  setEndAngle(endAngle: number): CircleSegment {
    this.endAngle = endAngle;
    return this;
  }

  /**
   * 克隆圆弧
   */
  clone(): CircleSegment {
    return new CircleSegment(
      this.center.clone(),
      this.radius,
      this.startAngle,
      this.endAngle,
      this.radian,
    );
  }

  /**
   * 获取圆弧的长度
   */
  get length(): number {
    const angleDiff = this.getSignedAngleDifference();
    return this.radius * Math.abs(angleDiff);
  }

  /**
   * 获取起始点
   */
  get startPoint(): Vec2d {
    return Vec2d.Add(this.center, Vec2d.FromAngle(this.startAngle, this.radius));
  }

  /**
   * 获取结束点
   */
  get endPoint(): Vec2d {
    return Vec2d.Add(this.center, Vec2d.FromAngle(this.endAngle, this.radius));
  }

  /**
   * 获取圆弧的中点
   */
  get midpoint(): Vec2d {
    const midAngle = this.startAngle + this.getSignedAngleDifference() / 2;
    return Vec2d.Add(this.center, Vec2d.FromAngle(midAngle, this.radius));
  }

  /**
   * 获取圆弧的角度差
   */
  getAngleDifference(): number {
    let diff = this.endAngle - this.startAngle;
    // 规范化角度差到 [0, 2π]
    while (diff < 0) diff += 2 * Math.PI;
    while (diff > 2 * Math.PI) diff -= 2 * Math.PI;
    return diff;
  }

  /**
   * 获取考虑方向的有符号角度差
   * radian = -1（逆时针）：返回 [0, 2π] 的正向角度差
   * radian = 1（顺时针）：返回非正的角度差（<= 0），表示沿顺时针方向的角度
   */
  private getSignedAngleDifference(): number {
    // 数学坐标系下的 CCW 正向差值（[0, 2π]）
    let ccw = this.endAngle - this.startAngle;
    while (ccw < 0) ccw += 2 * Math.PI;
    while (ccw > 2 * Math.PI) ccw -= 2 * Math.PI;

    // 屏幕坐标系 y 向下：视觉上的“逆时针”应沿负角度方向前进
    // radian = -1（逆时针，视觉）→ 返回 -ccw；radian = 1（顺时针，视觉）→ 返回 +ccw
    return this.radian === -1 ? -ccw : ccw;
  }

  /**
   * 获取圆弧的包围盒
   */
  get boundingBox(): { min: Vec2d; max: Vec2d } {
    // 计算圆弧的关键点
    const points = [this.startPoint, this.endPoint];
    
    // 检查中间的关键角度点（0, π/2, π, 3π/2）
    const criticalAngles = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2];
    for (const angle of criticalAngles) {
      if (this.containsAngle(angle)) {
        points.push(Vec2d.Add(this.center, Vec2d.FromAngle(angle, this.radius)));
      }
    }
    
    return GeometryUtils.boundingBox(points);
  }

  /**
   * 判断给定的角度是否在圆弧范围内
   */
  containsAngle(angle: number): boolean {
    // 采用“从 startAngle 沿弧向（由 radian 决定）走到 endAngle”的定义
    const normPos = (a: number) => ((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    const start = normPos(this.startAngle);
    // const end = normPos(this.endAngle);
    const ang = normPos(angle);

    const signedDiff = this.getSignedAngleDifference();

    if (signedDiff === 0) return normPos(ang) === normPos(start); // 退化情况

    if (signedDiff > 0) {
      // 正向（顺时针，视觉）沿角度递增方向
      const forward = normPos(ang - start); // [0, 2π)
      return forward <= signedDiff;
    } else {
      // 负向（逆时针，视觉）沿角度递减方向
      const backward = normPos(start - ang); // [0, 2π)
      return -backward >= signedDiff; // backward <= |signedDiff|
    }
  }

  /**
   * 计算点到圆弧的距离
   */
  distanceToPoint(point: VecLike): number {
    const nearestPoint = this.nearestPointOnSegment(point);
    return Vec2d.Dist(point, nearestPoint);
  }

  /**
   * 获取圆弧上距离指定点最近的点
   */
  nearestPointOnSegment(point: VecLike, clamp = true): Vec2d {
    const vecFromCenter = Vec2d.Sub(point, this.center);
    const angle = Math.atan2(vecFromCenter.y, vecFromCenter.x);
    
    let targetAngle = angle;
    
    if (clamp && !this.containsAngle(angle)) {
      // 找到最近的端点
      const distToStart = Vec2d.Dist(this.startPoint, point);
      const distToEnd = Vec2d.Dist(this.endPoint, point);
      targetAngle = distToStart < distToEnd ? this.startAngle : this.endAngle;
    }
    
    return Vec2d.Add(this.center, Vec2d.FromAngle(targetAngle, this.radius));
  }

  /**
   * 判断点是否在圆弧上（考虑容差）
   */
  containsPoint(point: VecLike, tolerance = 0.0001): boolean {
    // 先检查点是否在圆上
    const distToCenter = Vec2d.Dist(point, this.center);
    if (Math.abs(distToCenter - this.radius) > tolerance) {
      return false;
    }
    
    // 检查点的角度是否在圆弧范围内
    const angle = Math.atan2(point.y - this.center.y, point.x - this.center.x);
    return this.containsAngle(angle);
  }

  /**
   * 计算与线段的交点
   */
  intersection(other: LineSegmentLike): Vec2d[] {
    const lineStart = Vec2d.Cast(other.start);
    const lineEnd = Vec2d.Cast(other.end);
    const intersections: Vec2d[] = [];
    
    // 计算线段的方向向量
    const direction = Vec2d.Sub(lineEnd, lineStart);
    const unitDir = Vec2d.Uni(direction);
    
    // 计算从圆心到线段起点的向量
    const toLineStart = Vec2d.Sub(lineStart, this.center);
    
    // 计算线段上最近的点到圆心的距离
    const a = Vec2d.Dpr(unitDir, unitDir);
    const b = 2 * Vec2d.Dpr(toLineStart, unitDir);
    const c = Vec2d.Len2(toLineStart) - this.radius * this.radius;
    
    const discriminant = b * b - 4 * a * c;
    
    if (discriminant < 0) {
      return intersections;
    }
    
    const sqrtDiscriminant = Math.sqrt(discriminant);
    const t1 = (-b - sqrtDiscriminant) / (2 * a);
    const t2 = (-b + sqrtDiscriminant) / (2 * a);
    
    // 检查交点是否在线段上
    if (t1 >= 0 && t1 <= 1) {
      const point = Vec2d.Lrp(lineStart, lineEnd, t1);
      const angle = Math.atan2(point.y - this.center.y, point.x - this.center.x);
      if (this.containsAngle(angle)) {
        intersections.push(point);
      }
    }
    
    if (t2 >= 0 && t2 <= 1 && Math.abs(t2 - t1) > 1e-10) {
      const point = Vec2d.Lrp(lineStart, lineEnd, t2);
      const angle = Math.atan2(point.y - this.center.y, point.x - this.center.x);
      if (this.containsAngle(angle)) {
        intersections.push(point);
      }
    }
    
    return intersections;
  }

  /**
   * 判断是否与线段相交
   */
  intersects(other: LineSegmentLike): boolean {
    return this.intersection(other).length > 0;
  }

  /**
   * 平移圆弧
   */
  translate(offset: VecLike): CircleSegment {
    this.center.add(offset);
    return this;
  }

  /**
   * 缩放圆弧（以圆心为中心）
   */
  scale(factor: number): CircleSegment {
    this.radius *= factor;
    return this;
  }

  /**
   * 缩放圆弧（以指定点为中心）
   */
  scaleWithOrigin(factor: number, origin: VecLike): CircleSegment {
    this.center.setTo(Vec2d.ScaleWithOrigin(this.center, factor, origin));
    this.radius *= factor;
    return this;
  }

  /**
   * 旋转圆弧（以圆心为中心）
   */
  rotate(angle: number): CircleSegment {
    this.startAngle += angle;
    this.endAngle += angle;
    return this;
  }

  /**
   * 旋转圆弧（以指定点为中心）
   */
  rotateWithOrigin(angle: number, origin: VecLike): CircleSegment {
    this.center.rotWith(origin, angle);
    this.startAngle += angle;
    this.endAngle += angle;
    return this;
  }

  /**
   * 将圆弧分割成指定数量的等长段
   */
  divide(segments: number): Vec2d[] {
    if (segments <= 1) return [this.startPoint, this.endPoint];
    
    const points: Vec2d[] = [];
    const angleDiff = this.getSignedAngleDifference();
    
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const angle = this.startAngle + angleDiff * t;
      points.push(Vec2d.Add(this.center, Vec2d.FromAngle(angle, this.radius)));
    }
    
    return points;
  }

  /**
   * 将圆弧分割成指定长度的段
   */
  divideByLength(segmentLength: number): Vec2d[] {
    const totalLength = this.length;
    const segments = Math.floor(totalLength / segmentLength);
    
    if (segments <= 0) return [this.startPoint, this.endPoint];
    
    const points: Vec2d[] = [this.startPoint];
    const angleDiff = this.getAngleDifference();
    
    for (let i = 1; i <= segments; i++) {
      const t = (i * segmentLength) / totalLength;
      const angle = this.startAngle + angleDiff * t;
      points.push(Vec2d.Add(this.center, Vec2d.FromAngle(angle, this.radius)));
    }
    
    if (!GeometryUtils.isNear(points[points.length - 1], this.endPoint)) {
      points.push(this.endPoint);
    }
    
    return points;
  }

  /**
   * 获取圆弧上的点（参数化）
   */
  getPointAt(t: number): Vec2d {
    const clampedT = Math.max(0, Math.min(1, t));
    const angleDiff = this.getSignedAngleDifference();
    const angle = this.startAngle + angleDiff * clampedT;
    return Vec2d.Add(this.center, Vec2d.FromAngle(angle, this.radius));
  }

  /**
   * 判断两个圆弧是否相等
   */
  equals(other: CircleSegmentLike, tolerance = 0.0001): boolean {
    const otherSegment = other instanceof CircleSegment ? other : CircleSegment.fromJson(other);
    
    return (
      GeometryUtils.isNear(this.center, otherSegment.center, tolerance) &&
      Math.abs(this.radius - otherSegment.radius) < tolerance &&
      Math.abs(this.startAngle - otherSegment.startAngle) < tolerance &&
      Math.abs(this.endAngle - otherSegment.endAngle) < tolerance &&
      this.radian === otherSegment.radian
    );
  }

  /**
   * 转换为JSON对象
   */
  toJson(): CircleSegmentModel {
    return {
      center: this.center.toJson(),
      radius: this.radius,
      startAngle: this.startAngle,
      endAngle: this.endAngle,
      radian: this.radian,
    };
  }

  /**
   * 转换为字符串
   */
  toString(): string {
    return `CircleSegment(center: ${this.center.toString()}, radius: ${this.radius.toFixed(2)}, angles: ${(this.startAngle * 180 / Math.PI).toFixed(1)}° - ${(this.endAngle * 180 / Math.PI).toFixed(1)}°)`;
  }

  /**
   * 静态方法：从圆心、半径和角度创建圆弧
   */
  static from(center: VecLike, radius: number, startAngle: number, endAngle: number, radian: 1 | -1 = -1): CircleSegment {
    return new CircleSegment(
      Vec2d.Cast(center),
      radius,
      startAngle,
      endAngle,
      radian,
    );
  }

  /**
   * 静态方法：从圆心和两个点创建圆弧
   */
  static fromPoints(center: VecLike, startPoint: VecLike, endPoint: VecLike, radian: 1 | -1 = -1): CircleSegment {
    const centerVec = Vec2d.Cast(center);
    const startVec = Vec2d.Cast(startPoint);
    const endVec = Vec2d.Cast(endPoint);
    
    const radius = Vec2d.Dist(centerVec, startVec);
    const startAngle = Math.atan2(startVec.y - centerVec.y, startVec.x - centerVec.x);
    const endAngle = Math.atan2(endVec.y - centerVec.y, endVec.x - centerVec.x);
    
    return new CircleSegment(centerVec, radius, startAngle, endAngle, radian);
  }

  /**
   * 静态方法：从JSON对象创建圆弧
   */
  static fromJson(json: CircleSegmentModel): CircleSegment {
    return new CircleSegment(
      Vec2d.From(json.center),
      json.radius,
      json.startAngle,
      json.endAngle,
      (json.radian ?? -1) as 1 | -1,
    );
  }

  /**
   * 静态方法：计算点到圆弧的距离
   */
  static distanceToPoint(segment: CircleSegmentLike, point: VecLike): number {
    const arc = segment instanceof CircleSegment ? segment : CircleSegment.fromJson(segment);
    return arc.distanceToPoint(point);
  }

  /**
   * 静态方法：判断点是否在圆弧上
   */
  static containsPoint(segment: CircleSegmentLike, point: VecLike, tolerance = 0.0001): boolean {
    const arc = segment instanceof CircleSegment ? segment : CircleSegment.fromJson(segment);
    return arc.containsPoint(point, tolerance);
  }

  /**
   * 静态工具：将 CircleSegmentLike 统一转换为 JSON 模型
   */
  static toJson(segment: CircleSegmentLike): CircleSegmentModel {
    return segment instanceof CircleSegment ? segment.toJson() : segment;
  }
}